import React, { useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { NodeRowProps } from '../../types';
import { Collapsible } from 'ncoded-component-library';
import FolderIcon from 'icons/Folder.icon';
import useNodeInfo from '../../hooks/useNodeInfo';
import FileAccessCheckboxes from '../FileAccessCheckboxes';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { NODE_COMPONENT } from '../../data';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import FileRowPlaceholder from '../FileRowPlaceholder';
import { useTranslation } from 'react-i18next';
import {
  getFileCountInFolder,
  getDataRoomNodeType,
  getShowNodeHasBuyerFiles,
  getArrayLength,
  getHasElementsInArray,
} from 'utils';

import './FolderNodeCollaps.styles.scss';

const FolderNodeCollaps: React.FC<NodeRowProps> = (props) => {
  const {
    className,
    node,
    refetchFlag,
    checkedFilesIds,
    checkedDownloadIds,
    hasDownload,
    orderStageCopies,
    onChange,
    getChildrenForNode,
    checkIfNodeIsDisabled,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [levelNodes, setLevelNodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isAlreadyFetched = useRef(false);

  const { _id, name, childrenIds } = node || {};

  const { popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const createInfoData = useNodeInfo();

  const childrenIdsLength = getArrayLength(childrenIds);
  const hasBuyerFiles = getShowNodeHasBuyerFiles(node);

  const classes = classNames('folder-node-collaps', className);

  const fileCount = getFileCountInFolder(node);

  useEffect(() => {
    if (isAlreadyFetched.current || !isOpen) return;

    (async () => {
      try {
        setIsLoading(true);

        const data = await getChildrenForNode(node?._id);

        setLevelNodes(data);
        isAlreadyFetched.current = true;
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [getChildrenForNode, isOpen, levelNodes, node, popServerError]);

  useEffect(() => setIsOpen(false), [refetchFlag]);

  return (
    <div className="wrapp">
      <Collapsible
        className={classes}
        open={isOpen}
        onAnimationEnd={setIsOpen}
        trigger={
          <div className="folder-node-collaps__trigger">
            <div className="file-access-selector__node-info">
              <ChevrondownIcon
                className={classNames('chevron-icon', { rotated: isOpen })}
              />
              <FolderIcon
                className={classNames('folder-icon', {
                  transparent: isOpen,
                })}
              />
              <span>
                <p className="name">{name}</p>
                {createInfoData(node)}
              </span>

              {!!fileCount && <span className="file-count">{fileCount}</span>}
            </div>

            <FileAccessCheckboxes
              {...props}
              id={_id}
              className="folder-node-collapse__actions"
            />

            {hasBuyerFiles && <p>{t('onlySellSideFilesWillBeShared')}</p>}
          </div>
        }
      >
        <ul className="file-access-selector__nodes">
          {isLoading ? (
            Array(childrenIdsLength || 3)
              .fill(null)
              .map((_, index) => <FileRowPlaceholder key={index} />)
          ) : getHasElementsInArray(levelNodes) ? (
            levelNodes.map((node) => {
              const nodeType = getDataRoomNodeType(node);

              return (
                <li
                  key={node._id}
                  className={`file-access-selector__${nodeType}`}
                >
                  {node &&
                    NODE_COMPONENT[nodeType]({
                      node,
                      refetchFlag,
                      checkedFilesIds,
                      checkedDownloadIds,
                      hasDownload,
                      orderStageCopies,
                      onChange,
                      getChildrenForNode,
                      checkIfNodeIsDisabled,
                    })}
                </li>
              );
            })
          ) : (
            <small>{t('emptyFolder')}</small>
          )}
        </ul>
      </Collapsible>
    </div>
  );
};

export default FolderNodeCollaps;
